/**
 * Catálogo de tasas de IVA del tenant. Cada tenant mantiene su propia
 * lista (por default IVA 19% se crea con el tenant; el admin puede
 * agregar 0%, 5%, 19%, exentos, etc.). Los productos referencian una
 * tarifa vía `products.taxRateId`.
 *
 * Operaciones: listar · crear · editar · desactivar (soft-delete).
 * No se hace hard-delete porque los productos pueden tener FKs
 * lógicas hacia la tarifa.
 */

import { Button, CollapsibleSection } from '@atreus/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Percent, Pencil, Plus, Power, X } from 'lucide-react';
import { useState } from 'react';

import { apiDelete, apiGet, apiPatch, apiPost } from '../../cloud.js';

interface TaxRate {
  id: string;
  name: string;
  rateBp: number;
  dianTaxCode: string | null;
  active: boolean;
}

interface ListResponse {
  taxRates: TaxRate[];
}

const formatBp = (bp: number): string => `${(bp / 100).toFixed(2)}%`;

export const TenantTaxRatesSection = ({ tenantId }: { tenantId: string }) => {
  const qc = useQueryClient();
  const queryKey = ['admin', 'tenants', tenantId, 'tax-rates'];
  const list = useQuery({
    queryKey,
    queryFn: () => apiGet<ListResponse>(`/admin/tenants/${tenantId}/tax-rates`),
  });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TaxRate | null>(null);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey });
  };

  return (
    <CollapsibleSection
      title="Tasas de IVA"
      icon={Percent}
      persistKey={`tenant-tax-rates-${tenantId}`}
    >
      <p className="mb-3 text-xs text-fg-muted">
        Tarifas disponibles al crear productos. Desactivar no borra la
        tarifa — los productos existentes la conservan, pero no aparece en
        selects nuevos.
      </p>

      {list.isLoading ? (
        <p className="text-fg-muted">Cargando…</p>
      ) : list.error ? (
        <p className="text-danger">{(list.error as Error).message}</p>
      ) : (
        <>
          <ul className="mb-3 divide-y divide-border rounded-md border border-border">
            {(list.data?.taxRates ?? []).map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{r.name}</span>
                    <span className="rounded bg-surface-muted px-2 py-0.5 font-mono text-xs">
                      {formatBp(r.rateBp)}
                    </span>
                    {!r.active ? (
                      <span className="text-xs text-danger">inactiva</span>
                    ) : null}
                  </div>
                  {r.dianTaxCode ? (
                    <div className="mt-0.5 font-mono text-[10px] text-fg-subtle">
                      DIAN: {r.dianTaxCode}
                    </div>
                  ) : null}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(r)}
                  title="Editar"
                >
                  <Pencil className="size-3.5" />
                </Button>
                <ToggleActiveButton
                  tenantId={tenantId}
                  rate={r}
                  onDone={invalidate}
                />
              </li>
            ))}
            {list.data && list.data.taxRates.length === 0 ? (
              <li className="px-3 py-2 text-sm text-fg-muted">
                Sin tarifas — agrega al menos una.
              </li>
            ) : null}
          </ul>

          <div className="flex justify-end">
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              Nueva tarifa
            </Button>
          </div>
        </>
      )}

      {creating ? (
        <TaxRateDialog
          tenantId={tenantId}
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={invalidate}
        />
      ) : null}
      {editing ? (
        <TaxRateDialog
          tenantId={tenantId}
          mode="edit"
          rate={editing}
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      ) : null}
    </CollapsibleSection>
  );
};

const ToggleActiveButton = ({
  tenantId,
  rate,
  onDone,
}: {
  tenantId: string;
  rate: TaxRate;
  onDone: () => void;
}) => {
  // Activar usa PATCH (active=true); desactivar usa DELETE (soft-delete).
  // Dos endpoints distintos porque DELETE es idempotente y explícito.
  const toggle = useMutation({
    mutationFn: () =>
      rate.active
        ? apiDelete<{ ok: true }>(
            `/admin/tenants/${tenantId}/tax-rates/${rate.id}`,
          )
        : apiPatch<{ ok: true }>(
            `/admin/tenants/${tenantId}/tax-rates/${rate.id}`,
            { active: true },
          ),
    onSuccess: onDone,
  });
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
      title={rate.active ? 'Desactivar' : 'Reactivar'}
    >
      <Power className="size-3.5" />
    </Button>
  );
};

const TaxRateDialog = ({
  tenantId,
  mode,
  rate,
  onClose,
  onSaved,
}: {
  tenantId: string;
  mode: 'create' | 'edit';
  rate?: TaxRate;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [name, setName] = useState(rate?.name ?? '');
  // Guardamos el porcentaje en el input (más humano) y lo convertimos a bp
  // al enviar. Permitimos hasta 2 decimales.
  const [ratePct, setRatePct] = useState(
    rate ? (rate.rateBp / 100).toFixed(2) : '',
  );
  const [dianTaxCode, setDianTaxCode] = useState(rate?.dianTaxCode ?? '');

  const save = useMutation({
    mutationFn: async (): Promise<unknown> => {
      const pct = Number(ratePct);
      const rateBp = Math.round(pct * 100);
      const body = {
        name: name.trim(),
        rateBp,
        dianTaxCode: dianTaxCode.trim() || null,
      };
      if (mode === 'create') {
        return apiPost<{ id: string }>(
          `/admin/tenants/${tenantId}/tax-rates`,
          body,
        );
      }
      return apiPatch<{ ok: true }>(
        `/admin/tenants/${tenantId}/tax-rates/${rate!.id}`,
        body,
      );
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const pctNum = Number(ratePct);
  const canSubmit =
    !save.isPending &&
    name.trim().length > 0 &&
    ratePct !== '' &&
    Number.isFinite(pctNum) &&
    pctNum >= 0 &&
    pctNum <= 100;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-raised shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-lg font-black tracking-tight">
            {mode === 'create' ? 'Nueva tarifa' : 'Editar tarifa'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
            <X className="size-4" />
          </Button>
        </header>
        <form
          className="space-y-3 p-5"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) save.mutate();
          }}
        >
          <div>
            <label className="mb-1 block text-sm font-semibold">Nombre</label>
            <input
              autoComplete="off"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="IVA 19%"
              className="w-full rounded-md border border-border bg-surface px-3 py-2"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">
              Porcentaje
            </label>
            <div className="flex items-center gap-2">
              <input
                autoComplete="off"
                inputMode="decimal"
                value={ratePct}
                onChange={(e) =>
                  setRatePct(e.target.value.replace(/[^\d.]/g, ''))
                }
                placeholder="19"
                className="w-32 rounded-md border border-border bg-surface px-3 py-2 text-right font-mono"
              />
              <span className="text-fg-muted">%</span>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">
              Código DIAN{' '}
              <span className="font-normal text-fg-muted">(opcional)</span>
            </label>
            <input
              autoComplete="off"
              value={dianTaxCode}
              onChange={(e) => setDianTaxCode(e.target.value)}
              placeholder="01"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono"
            />
          </div>

          {save.error ? (
            <p className="text-sm text-danger">
              {(save.error as Error).message}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {save.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
